import { ALL_LOCATIONS_HREF } from './locations'

/**
 * Site-wide configuration.
 *
 * Navigation is declared once here and consumed by both the desktop header and
 * the mobile drawer, so the two can never drift apart.
 */

export const SITE = {
  name: 'Contractor Wiki',
  url: 'https://contractor.wiki',
  description:
    'Find and contact trusted local contractors across the United States. Browse by state, city and service.',
  /** Announcement bar, wide viewports. */
  tagline: 'Helping homeowners connect with trusted local professionals',
  /** Announcement bar, narrow viewports — the desktop content does not fit. */
  taglineShort: 'Find trusted local contractors',
} as const

/**
 * The viewport at which the desktop navigation replaces the drawer.
 *
 * This mirrors `--breakpoint-nav` in `globals.css`, which is what actually
 * drives the layout. The two must be changed together — this copy exists only
 * so the drawer can react to the same threshold in JavaScript.
 */
export const NAV_BREAKPOINT = '(min-width: 70rem)'

export interface NavItem {
  label: string
  href: string
  /**
   * Marks an item that will later open a panel of states, cities or services.
   * Today it only renders the affordance — there is no menu behind it yet.
   */
  hasMenu?: boolean
}

/**
 * Where a homeowner describes a job. A primary conversion action, which is
 * why it earns a place in the main navigation.
 */
export const START_PROJECT_HREF = '/start-project'

/**
 * The contractor's own working area — dashboard and profile editor.
 *
 * Declared here rather than beside the components so the header and the
 * dashboard cannot drift onto two spellings of one route, which has already
 * happened twice on this site.
 */
export const BUSINESS_HREF = '/business'
export const BUSINESS_PROFILE_HREF = '/business/profile'

/**
 * Domain registration — MTC-CTW-008.
 *
 * `/domains` is public: somebody deciding whether Contractor Wiki is worth
 * using may well be shopping for a domain first. The list of domains you
 * own sits inside the business area, because it is your account's property.
 */
export const DOMAINS_HREF = '/domains'
export const BUSINESS_DOMAINS_HREF = '/business/domains'

/**
 * The website builder — MTC-CTW-010.
 *
 * Public, like /domains: a contractor deciding whether Contractor Wiki is
 * worth joining may well want to see what their site would look like first.
 */
export const PAGE_BUILDER_HREF = '/page-builder'

/**
 * The second funnel: contractors who want a custom site rather than a
 * template. Nate asked for both halves of the market, and a builder with no
 * way out of it only serves one.
 */
export const CUSTOM_WEBSITE_HREF = '/custom-website'

/** Primary navigation, in display order. */
export const PRIMARY_NAV: readonly NavItem[] = [
  { label: 'Find Contractors', href: '/contractors', hasMenu: true },
  /**
   * Points at the canonical location directory. It used to say '/cities',
   * a route that never existed, while the footer linked '/locations' — one
   * destination, two paths, and the header's was dead.
   */
  { label: 'Browse Cities', href: ALL_LOCATIONS_HREF },
  { label: 'Services', href: '/services', hasMenu: true },
  { label: 'How It Works', href: '/how-it-works' },
  /**
   * Last, not first: somebody who already knows what they need goes to
   * Find Contractors. This is for the visitor who would rather describe
   * the job and be found instead.
   */
  { label: 'Start a Project', href: START_PROJECT_HREF },
] as const

/**
 * TEMPORARY: stands in for real location state.
 *
 * Every component that needs "where are we looking" reads this, so when the
 * location becomes real — a picker, a saved preference, an IP guess — it
 * changes here and nowhere else. Nothing hard-codes a city name.
 */
export const DEFAULT_LOCATION = { city: 'Boise', state: 'ID' } as const

export const locationLabel = (l: { city: string; state: string }) => `${l.city}, ${l.state}`

/** Contractor profile. The slug is a public, permanent URL once minted. */
export const contractorHref = (slug: string) => `/contractors/${slug}`

/** Search and browse across every contractor. */
export const ALL_CONTRACTORS_HREF = '/contractors'

/**
 * Contractor-side routes, namespaced under /contractors so the whole
 * business-owner journey shares one prefix rather than scattering top-level
 * paths. None of these flows exist yet.
 */
export const CLAIM_BUSINESS_HREF = '/contractors/claim'
export const LIST_BUSINESS_HREF = '/contractors/list-business'

/**
 * Where a contractor learns about paid placement.
 *
 * Declared here so the search results page has a real destination to point
 * at rather than a placeholder, but the page itself is MTC-CTW-002 Part 2
 * and does not exist yet — this route does not resolve until it is built.
 */
export const PROMOTE_BUSINESS_HREF = '/promote-business'

/** Account and business actions, shown apart from the primary navigation. */
export const ACTIONS = {
  signIn: { label: 'Sign In', href: '/sign-in' },
  /**
   * Points at the same route the footer uses. These were two different
   * paths for one destination, so the header CTA and the footer link led to
   * different places — both of them nowhere.
   */
  listBusiness: { label: 'List Your Business', href: LIST_BUSINESS_HREF },
  forContractors: { label: 'For Contractors', href: '/for-contractors' },
  /**
   * A contractor's dashboard. Sits with the other contractor-facing links
   * rather than in the main navigation, because a homeowner has no business
   * area and should not be offered one.
   */
  myBusiness: { label: 'My Business', href: BUSINESS_HREF },
} as const satisfies Record<string, NavItem>

/* ============================================================================
 * FOOTER NAVIGATION
 * ----------------------------------------------------------------------------
 * Shared by the desktop columns and the mobile accordions, so the two can
 * never drift. Routes reuse the constants above rather than restating paths.
 *
 * Deliberately short. A directory footer is where every state, city and
 * service wants to end up; that belongs on the browse pages, not here.
 * ========================================================================= */
export interface FooterGroup {
  id: string
  heading: string
  links: readonly NavItem[]
}

export const FOOTER_NAV: readonly FooterGroup[] = [
  {
    id: 'homeowners',
    heading: 'Homeowners',
    links: [
      { label: 'Find Contractors', href: ALL_CONTRACTORS_HREF },
      { label: 'Browse Services', href: '/services' },
      { label: 'Browse Cities', href: '/locations' },
      { label: 'How It Works', href: '/how-it-works' },
    ],
  },
  {
    id: 'contractors',
    heading: 'Contractors',
    links: [
      { label: 'Find My Business', href: CLAIM_BUSINESS_HREF },
      { label: 'List Your Business', href: LIST_BUSINESS_HREF },
      { label: 'Contractor Sign In', href: '/sign-in' },
    ],
  },
  {
    id: 'company',
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Help', href: '/help' },
    ],
  },
]

export const LEGAL_LINKS: readonly NavItem[] = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Accessibility', href: '/accessibility' },
]

